// Lesson world files are GitHub blob URLs, and private official lessons carry a token in them
// (https://github_pat_…@github.com/…). Pages show links built from these helpers, never the raw URL.

export interface GitHubLocation {
  owner: string;
  repo: string;
  ref: string;
  // Path of the world file inside the repository, e.g. "visual_tracking/worlds/visual_tracking.wbt"
  path: string;
}

export function parseGitHubWorldUrl(worldFile: string | null | undefined): GitHubLocation | null {
  if (!worldFile) {
    return null;
  }
  const match = /^https:\/\/(?:[^@/]+@)?github\.com\/([^/]+)\/([^/]+)\/blob\/([^/]+)\/(.+)$/.exec(worldFile.trim());
  if (!match) {
    return null;
  }
  const [, owner, repo, ref, path] = match;
  return { owner, repo, ref, path };
}

export function isGitHubWorldUrl(worldFile: string | null | undefined): boolean {
  return parseGitHubWorldUrl(worldFile) !== null;
}

// Token-free link to the world file.
export function gitHubWorldLink(worldFile: string | null | undefined): string | null {
  const location = parseGitHubWorldUrl(worldFile);
  return location ? `https://github.com/${location.owner}/${location.repo}/blob/${location.ref}/${location.path}` : null;
}

// Token-free link to a file of the lesson's Webots project (the folder above worlds/), e.g. the editable controller.
export function gitHubProjectFileLink(worldFile: string | null | undefined, relativePath: string | null | undefined): string | null {
  const location = parseGitHubWorldUrl(worldFile);
  if (!location || !relativePath) {
    return null;
  }
  const worldsIndex = location.path.lastIndexOf('worlds/');
  const projectDir = worldsIndex >= 0 ? location.path.slice(0, worldsIndex) : location.path.slice(0, location.path.lastIndexOf('/') + 1);
  return `https://github.com/${location.owner}/${location.repo}/blob/${location.ref}/${projectDir}${relativePath.replace(/^\/+/, '')}`;
}

// "owner/repo/path/to/world.wbt", for display.
export function gitHubWorldLabel(worldFile: string | null | undefined): string {
  const location = parseGitHubWorldUrl(worldFile);
  if (!location) {
    // Not a GitHub URL: still never show credentials.
    return (worldFile || '').replace(/\/\/[^@/]+@/, '//');
  }
  return `${location.owner}/${location.repo}/${location.path}`;
}
